import { Button } from '@shared/ui/Button';
import { Card, Input, Muted, Row, Small, Table, TableBox } from '@widgets/ksp-workspace/parts';
import type { KspRdImport, KspRdPlan } from '@entities/ksp/types';
import { ROW_STATUS, ru } from '../types';

export interface RdPlanImportsCardProps {
  canEdit: boolean;
  file: File | null;
  setFile: (file: File | null) => void;
  uploading: boolean;
  sendFile: () => void;
  planForm: () => void;
  imports: KspRdImport[];
  openImport: string;
  setOpenImport: (id: string) => void;
  plans: KspRdPlan[];
  counts: (imp: KspRdImport) => string;
  onAcceptImport: (impId: string) => void;
  onLink: (plan: KspRdPlan) => void;
  onRemovePlan: (plan: KspRdPlan) => void;
}

export function RdPlanImportsCard({
  canEdit,
  file,
  setFile,
  uploading,
  sendFile,
  planForm,
  imports,
  openImport,
  setOpenImport,
  plans,
  counts,
  onAcceptImport,
  onLink,
  onRemovePlan,
}: RdPlanImportsCardProps) {
  return (
    <Card>
      <h3>График выдачи РД (плановые даты)</h3>
      <Muted>
        Форма Заказчика: «Обозначение документации», «Наименование комплекта», «Дата выдачи
        комплекта». Загрузка файла прогноз не меняет — применяются только принятые однозначные
        строки; плановая дата не подменяет подтверждённую.
      </Muted>
      <Row>
        <Input
          type="file"
          accept=".xlsx,.xlsm,.csv"
          disabled={!canEdit}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <Button disabled={!canEdit || !file || uploading} onClick={sendFile}>
          Загрузить график выдачи
        </Button>
        <Button $variant="ghost" disabled={!canEdit} onClick={planForm}>
          Плановая дата вручную
        </Button>
      </Row>
      {imports.map((imp) => (
        <Card key={imp.id}>
          <Row>
            <strong>{imp.filename}</strong>
            <Small>
              SHA-256 {imp.file_sha256.slice(0, 12)}… · строк {imp.row_count} ·{' '}
              {imp.status === 'needs_review' ? 'ждёт проверки' : imp.status === 'accepted' ? 'принят' : 'отклонён'}
            </Small>
            <Button $variant="ghost" $size="sm" onClick={() => setOpenImport(openImport === imp.id ? '' : imp.id)}>
              {openImport === imp.id ? 'Скрыть строки' : 'Показать строки'}
            </Button>
            {imp.status === 'needs_review' && (
              <Button $size="sm" disabled={!canEdit} onClick={() => onAcceptImport(imp.id)}>
                Принять однозначные строки
              </Button>
            )}
          </Row>
          <Small>{counts(imp)}</Small>
          {imp.issues.map((i, n) => (
            <Small key={n}>
              {i.cell ? `${i.cell}: ` : ''}
              {i.message}
            </Small>
          ))}
          {openImport === imp.id && (
            <TableBox>
              <Table>
                <thead>
                  <tr>
                    <th>Строка</th>
                    <th>Шифр</th>
                    <th>Наименование</th>
                    <th>Дата в файле</th>
                    <th>Плановая дата</th>
                    <th>Сопоставление</th>
                  </tr>
                </thead>
                <tbody>
                  {imp.rows.map((r) => (
                    <tr key={`${r.sheet}:${r.row}`}>
                      <td>
                        {r.sheet}!{r.date_cell}
                      </td>
                      <td>{r.code}</td>
                      <td>{r.title}</td>
                      <td>{r.date_raw}</td>
                      <td>{ru(r.planned_on)}</td>
                      <td>{ROW_STATUS[r.status] ?? r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableBox>
          )}
        </Card>
      ))}
      {plans.length > 0 && (
        <TableBox>
          <Table>
            <thead>
              <tr>
                <th>Комплект</th>
                <th>Плановая выдача</th>
                <th>Источник</th>
                <th>Реестр ПТО</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.set_code}
                    {p.title && <Small>{p.title}</Small>}
                  </td>
                  <td>{ru(p.planned_on)}</td>
                  <td>
                    {p.import_id
                      ? `файл, ${p.source_ref.sheet ?? ''} ${p.source_ref.date_cell ?? ''}: «${p.source_ref.value ?? ''}»`
                      : `вручную: ${p.reason ?? ''}`}
                  </td>
                  <td>{p.document_id ? 'в реестре' : 'ожидается'}</td>
                  <td>
                    {!p.document_id && (
                      <Button $size="sm" $variant="ghost" disabled={!canEdit} onClick={() => onLink(p)}>
                        Связать
                      </Button>
                    )}
                    <Button
                      $size="sm"
                      $variant="ghost"
                      disabled={!canEdit}
                      onClick={() => onRemovePlan(p)}
                    >
                      Снять
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableBox>
      )}
    </Card>
  );
}
